/**
 * @author: lv.chen3
 * @desc: KPI Overview页面存在指标卡片被隐藏时，页面底部展示的提示，可以通过点击提示内的按钮，切换展示无数据卡片、隐藏无数据卡片
 * @date: 2025/7/24
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { LANGUAGE_EN, LANGUAGE_CN } from '@home/_config/config';

import './index.scss';

/**
 * MetricCardHiddenTips组件props
 */
interface IMetricCardHiddenTipsProps extends IBaseProps {
  /**
   * 是否展示无数据的卡片
   */
  show: boolean;
  /**
   * 点击展开的回调
   */
  onShow?: () => void;
  /**
   * 点击隐藏的回调
   */
  onHide?: () => void;
}

/**
 * 渲染隐藏无数据卡片时的提示内容
 * @param onShow 点击展开的回调
 */
const renderHideTips = (onShow?: () => void): IRecord<React.ReactElement> => {
  return {
    [LANGUAGE_EN]: (
      <div>
        Only display metric cards with data.{' '}
        <span
          className="dbi-highlight-color dbi-cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onShow?.();
          }}
        >
          Show all
        </span>{' '}
        no-data metric cards on this page.{' '}
      </div>
    ),
    [LANGUAGE_CN]: (
      <div>
        只展示有数据的指标卡片。
        <span
          className="dbi-highlight-color dbi-cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onShow?.();
          }}
        >
          展开
        </span>
        本页面所有无数据指标卡片。
      </div>
    ),
  };
};

/**
 * 渲染展开无数据卡片的时的提示内容
 * @param onHide 点击隐藏的回调
 */
const renderShowTips = (onHide?: () => void): IRecord<React.ReactElement> => {
  return {
    [LANGUAGE_EN]: (
      <div>
        Display all metric cards.{' '}
        <span
          className="dbi-highlight-color dbi-cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onHide?.();
          }}
        >
          Hide all
        </span>{' '}
        no-data metric cards on this page.
      </div>
    ),
    [LANGUAGE_CN]: (
      <div>
        展示所有指标卡片。
        <span
          className="dbi-highlight-color dbi-cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onHide?.();
          }}
        >
          隐藏
        </span>
        本页面所有无数据指标卡片。
      </div>
    ),
  };
};

const MetricCardHiddenTips: React.FC<IMetricCardHiddenTipsProps> = (props) => {
  const { style, className, show, onShow, onHide } = props;

  const { locale } = useIntl();

  const localeTipMap: IRecord<React.ReactElement> = show ? renderShowTips(onHide) : renderHideTips(onShow);

  return (
    <div style={style} className={classnames('dbi-flex-center dbi-game-details-kpi-overview-no-data-tips', className)}>
      {localeTipMap[locale] || localeTipMap[LANGUAGE_EN]}
    </div>
  );
};

export default MetricCardHiddenTips;
